/**
 * ActivityEditPanel — the EDITABLE Time Log for one daily report.
 *
 * A sibling of the read-only `NptPanel`: it lists the day's activity rows
 * (scoped to `reportId`) with a per-row **Edit** trigger and a **"+ Agregar"**
 * trigger that both OPEN a stylized `<Modal>` (the house rule: edits open in a
 * modal, not an inline expanded row). Writes go to POST (add) / PUT keyed on
 * `entity_id` (edit), then refetch the event's activities list on success.
 *
 * Every picklist-backed field is a dropdown via the shared `PicklistSelect`:
 * Fase → activity_phases, Código → activity_codes, Subcódigo → activity_subcodes,
 * Motivo NPT → npt_categories. Clase (time_class) is a fixed enum; Nivel NPT is a
 * small numeric select; Clase NPT falls back to text when npt_classes is empty.
 *
 * Prop signature the lead mounts:
 *   ActivityEditPanel({ reportId, eventId }: { reportId: string; eventId: string })
 */
import { useMemo, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { Modal } from "@components/ui/Modal";
import { PicklistSelect } from "@components/ui/PicklistSelect";
import { useGetEventActivitiesQuery } from "@api/pApi";
import { useGetResolvedCatalogQuery } from "@api/mApi";
import { fmtDepth, fmtNum, intlTag } from "@lib/format";
import type { ActivityOut } from "@api/types";
import type { Locale } from "@i18n/locales";
import {
  useCreateActivityMutation,
  useUpdateActivityMutation,
  type ActivityRow,
  type ActivityWrite,
} from "./activityApi";

/** Wall-clock HH:MM for a time-log timestamp, in the tenant's locale. */
function fmtClock(iso: string | null, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString(intlTag(locale), { hour: "2-digit", minute: "2-digit" });
}

const TIME_CLASSES = ["productive", "npt", "flat_waiting"] as const;
type TimeClass = (typeof TIME_CLASSES)[number];

/** NPT level is a small fixed enum (1–3) per Oxbo-M's npt_levels catalog. */
const NPT_LEVELS = ["1", "2", "3"] as const;

/** The editable form shape (all optional; strings for the number inputs). */
interface FormState {
  sequence_no: string;
  time_from: string;
  time_to: string;
  duration_hours: string;
  md_from: string;
  md_to: string;
  phase_entry_key: string;
  activity_code_entry_key: string;
  activity_subcode_entry_key: string;
  time_class: string;
  npt_level: string;
  npt_class_entry_key: string;
  npt_reason_key: string;
  activity_memo: string;
}

const EMPTY_FORM: FormState = {
  sequence_no: "",
  time_from: "",
  time_to: "",
  duration_hours: "",
  md_from: "",
  md_to: "",
  phase_entry_key: "",
  activity_code_entry_key: "",
  activity_subcode_entry_key: "",
  time_class: "",
  npt_level: "",
  npt_class_entry_key: "",
  npt_reason_key: "",
  activity_memo: "",
};

/** An ISO timestamp → the "HH:MM" value a <input type=time> expects. */
function isoToTimeInput(iso: string | null, locale: Locale): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Present wall-clock in the tenant locale so round-tripping is stable.
  return d.toLocaleTimeString(intlTag(locale), {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function rowToForm(a: ActivityRow, locale: Locale): FormState {
  return {
    sequence_no: a.sequence_no?.toString() ?? "",
    time_from: isoToTimeInput(a.time_from, locale),
    time_to: isoToTimeInput(a.time_to, locale),
    duration_hours: a.duration_hours?.toString() ?? "",
    md_from: a.md_from?.toString() ?? "",
    md_to: a.md_to?.toString() ?? "",
    phase_entry_key: a.phase_entry_key ?? "",
    activity_code_entry_key: a.activity_code_entry_key ?? "",
    activity_subcode_entry_key: a.activity_subcode_entry_key ?? "",
    time_class: a.time_class ?? "",
    npt_level: a.npt_level?.toString() ?? "",
    npt_class_entry_key: a.npt_class_entry_key ?? "",
    npt_reason_key: a.npt_reason_key ?? "",
    activity_memo: a.activity_memo ?? "",
  };
}

/** null for a blank string, else the trimmed string. */
function orNull(s: string): string | null {
  const v = s.trim();
  return v === "" ? null : v;
}

/** null for a blank string, else a finite number (blank/invalid → null). */
function numOrNull(s: string): number | null {
  const v = s.trim();
  if (v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** A "HH:MM" time input → an ISO timestamp anchored on the report date. */
function timeInputToIso(hhmm: string, reportDate: string | null | undefined): string | null {
  const v = hhmm.trim();
  if (v === "") return null;
  // Anchor on the report date (or today if absent) so time-of-day round-trips.
  const day = reportDate && reportDate.length >= 10 ? reportDate.slice(0, 10) : new Date().toISOString().slice(0, 10);
  const iso = new Date(`${day}T${v}:00`);
  if (Number.isNaN(iso.getTime())) return null;
  return iso.toISOString();
}

function formToBody(f: FormState, reportDate: string | null | undefined): ActivityWrite {
  const tc = TIME_CLASSES.includes(f.time_class as TimeClass)
    ? (f.time_class as TimeClass)
    : null;
  return {
    sequence_no: numOrNull(f.sequence_no),
    time_from: timeInputToIso(f.time_from, reportDate),
    time_to: timeInputToIso(f.time_to, reportDate),
    duration_hours: numOrNull(f.duration_hours),
    md_from: numOrNull(f.md_from),
    md_to: numOrNull(f.md_to),
    phase_entry_key: orNull(f.phase_entry_key),
    activity_code_entry_key: orNull(f.activity_code_entry_key),
    activity_subcode_entry_key: orNull(f.activity_subcode_entry_key),
    time_class: tc,
    npt_level: numOrNull(f.npt_level),
    npt_class_entry_key: orNull(f.npt_class_entry_key),
    npt_reason_key: orNull(f.npt_reason_key),
    activity_memo: orNull(f.activity_memo),
  };
}

/** A row from the shared read type carries entity_id at runtime; expose it. */
function asRow(a: ActivityOut): ActivityRow {
  return a as ActivityRow;
}

/** HTTP status of an RTK Query error, if it's a fetch error with a status. */
function errStatus(err: unknown): number | null {
  if (err && typeof err === "object" && "status" in err) {
    const s = (err as { status: unknown }).status;
    if (typeof s === "number") return s;
  }
  return null;
}

export function ActivityEditPanel({
  reportId,
  eventId,
}: {
  reportId: string;
  eventId: string;
}) {
  const { t, locale } = useLocale();
  const {
    data: activities = [],
    isLoading,
    isError,
    refetch,
  } = useGetEventActivitiesQuery(eventId, { skip: !eventId });

  // Catalogs used for the read table's label resolution (the modal fields load
  // their own catalogs via PicklistSelect).
  const { data: phaseCat } = useGetResolvedCatalogQuery({ categoryKey: "activity_phases" });
  const { data: codeCat } = useGetResolvedCatalogQuery({ categoryKey: "activity_codes" });

  const phaseEntries = phaseCat?.entries ?? [];
  const codeEntries = codeCat?.entries ?? [];

  const codeLabels = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of codeEntries) m.set(e.entry_key, e.display_label);
    return m;
  }, [codeEntries]);
  const phaseLabels = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of phaseEntries) m.set(e.entry_key, e.display_label);
    return m;
  }, [phaseEntries]);

  // Only this day's rows (scoped to reportId).
  const rows = useMemo(
    () => activities.map(asRow).filter((a) => a.daily_report_id === reportId),
    [activities, reportId],
  );
  // The report date the time inputs anchor on (from any row of the day).
  const reportDate = rows[0]?.report_date ?? null;

  // Modal state: which entity is open (or "new"), and the form buffer.
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const [createActivity, createState] = useCreateActivityMutation();
  const [updateActivity, updateState] = useUpdateActivityMutation();
  const busy = createState.isLoading || updateState.isLoading;
  const mutError = createState.error ?? updateState.error;
  const is403 = errStatus(mutError) === 403;

  function setField<K extends keyof FormState>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function openAdd() {
    setForm({ ...EMPTY_FORM, sequence_no: String(rows.length + 1) });
    setEditing("new");
  }
  function openEdit(a: ActivityRow) {
    setForm(rowToForm(a, locale));
    setEditing(a.entity_id);
  }
  function cancel() {
    setEditing(null);
  }

  async function submit() {
    try {
      if (editing === "new") {
        await createActivity({ reportId, eventId, body: formToBody(form, reportDate) }).unwrap();
      } else if (editing) {
        await updateActivity({
          reportId,
          entityId: editing,
          eventId,
          body: formToBody(form, reportDate),
        }).unwrap();
      }
      setEditing(null);
      refetch();
    } catch {
      /* error surfaced via mutError in the modal footer */
    }
  }

  const modalTitle = editing === "new" ? t("actEdit.add") : t("actEdit.edit");

  return (
    <QueryState isLoading={isLoading} isError={isError} onRetry={refetch}>
      <div className="card">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <h3 style={{ margin: 0 }}>{t("actEdit.title")}</h3>
        </div>

        {rows.length === 0 ? (
          <div className="empty-state">{t("actEdit.empty")}</div>
        ) : (
          <div className="table-scroll" style={{ marginTop: 10 }}>
            <table>
              <thead>
                <tr>
                  <th className="num">{t("actEdit.f.seq")}</th>
                  <th>{t("actEdit.col.time")}</th>
                  <th className="num">{t("actEdit.f.duration")}</th>
                  <th>{t("actEdit.f.phase")}</th>
                  <th>{t("actEdit.f.code")}</th>
                  <th className="num">{t("actEdit.col.depth")}</th>
                  <th>{t("actEdit.f.timeClass")}</th>
                  <th>{t("actEdit.f.memo")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} data-owner="p">
                    <td className="num tab-nums">{a.sequence_no ?? "—"}</td>
                    <td className="mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                      {fmtClock(a.time_from, locale)} – {fmtClock(a.time_to, locale)}
                    </td>
                    <td className="num tab-nums">
                      {fmtNum(a.duration_hours, locale, { maximumFractionDigits: 2 })}
                    </td>
                    <td>
                      {a.phase_entry_key
                        ? (phaseLabels.get(a.phase_entry_key) ?? a.phase_entry_key)
                        : "—"}
                    </td>
                    <td>
                      <span className="mono">
                        {a.activity_code_entry_key
                          ? (codeLabels.get(a.activity_code_entry_key) ?? a.activity_code_entry_key)
                          : "—"}
                      </span>
                    </td>
                    <td className="num tab-nums">
                      {a.md_from != null || a.md_to != null
                        ? `${fmtDepth(a.md_from, locale)} – ${fmtDepth(a.md_to, locale)}`
                        : "—"}
                    </td>
                    <td>{a.time_class ?? "—"}</td>
                    <td style={{ color: "var(--dim)", maxWidth: 260 }}>{a.activity_memo ?? "—"}</td>
                    <td>
                      <button type="button" className="btn" onClick={() => openEdit(a)}>
                        {t("actEdit.edit")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <button
          type="button"
          className="btn primary"
          onClick={openAdd}
          style={{ marginTop: 10 }}
        >
          {t("actEdit.add")}
        </button>
      </div>

      <Modal
        open={editing !== null}
        title={modalTitle}
        onClose={cancel}
        onConfirm={submit}
        busy={busy}
        confirmLabel={busy ? t("actEdit.saving") : t("actEdit.save")}
        cancelLabel={t("actEdit.cancel")}
        footerNote={
          mutError ? (is403 ? t("actEdit.forbidden") : t("actEdit.error")) : null
        }
      >
        <div className="modal-grid">
          <label>
            <span>{t("actEdit.f.seq")}</span>
            <input
              type="number"
              value={form.sequence_no}
              onChange={(e) => setField("sequence_no", e.target.value)}
            />
          </label>
          <label>
            <span>{t("actEdit.f.timeFrom")}</span>
            <input
              type="time"
              value={form.time_from}
              onChange={(e) => setField("time_from", e.target.value)}
            />
          </label>
          <label>
            <span>{t("actEdit.f.timeTo")}</span>
            <input
              type="time"
              value={form.time_to}
              onChange={(e) => setField("time_to", e.target.value)}
            />
          </label>
          <label>
            <span>{t("actEdit.f.duration")}</span>
            <input
              type="number"
              step="0.25"
              value={form.duration_hours}
              onChange={(e) => setField("duration_hours", e.target.value)}
            />
          </label>
          <label>
            <span>{t("actEdit.f.mdFrom")}</span>
            <input
              type="number"
              step="any"
              value={form.md_from}
              onChange={(e) => setField("md_from", e.target.value)}
            />
          </label>
          <label>
            <span>{t("actEdit.f.mdTo")}</span>
            <input
              type="number"
              step="any"
              value={form.md_to}
              onChange={(e) => setField("md_to", e.target.value)}
            />
          </label>
          <PicklistSelect
            categoryKey="activity_phases"
            label={t("actEdit.f.phase")}
            placeholder={t("actEdit.select")}
            value={form.phase_entry_key}
            onChange={(v) => setField("phase_entry_key", v)}
          />
          <PicklistSelect
            categoryKey="activity_codes"
            label={t("actEdit.f.code")}
            placeholder={t("actEdit.select")}
            value={form.activity_code_entry_key}
            onChange={(v) => setField("activity_code_entry_key", v)}
          />
          <PicklistSelect
            categoryKey="activity_subcodes"
            label={t("actEdit.f.subcode")}
            placeholder={t("actEdit.select")}
            value={form.activity_subcode_entry_key}
            onChange={(v) => setField("activity_subcode_entry_key", v)}
          />
          <label>
            <span>{t("actEdit.f.timeClass")}</span>
            <select
              value={form.time_class}
              onChange={(e) => setField("time_class", e.target.value)}
            >
              <option value="">{t("actEdit.select")}</option>
              {TIME_CLASSES.map((c) => (
                <option key={c} value={c}>
                  {t(`actEdit.timeClass.${c}` as Parameters<typeof t>[0])}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{t("actEdit.f.nptLevel")}</span>
            <select
              value={form.npt_level}
              onChange={(e) => setField("npt_level", e.target.value)}
            >
              <option value="">{t("actEdit.select")}</option>
              {NPT_LEVELS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          {/* npt_classes resolves empty on Oxbo-M → falls back to a text input. */}
          <PicklistSelect
            categoryKey="npt_classes"
            label={t("actEdit.f.nptClass")}
            placeholder={t("actEdit.select")}
            value={form.npt_class_entry_key}
            onChange={(v) => setField("npt_class_entry_key", v)}
          />
          <PicklistSelect
            categoryKey="npt_categories"
            label={t("actEdit.f.nptReason")}
            placeholder={t("actEdit.select")}
            value={form.npt_reason_key}
            onChange={(v) => setField("npt_reason_key", v)}
          />
          <label className="modal-span-all">
            <span>{t("actEdit.f.memo")}</span>
            <input
              value={form.activity_memo}
              onChange={(e) => setField("activity_memo", e.target.value)}
            />
          </label>
        </div>
      </Modal>
    </QueryState>
  );
}
